/**
 * Один объект на стене на плане: розетка, выключатель, коробка, смеситель.
 *
 * Объект сидит на грани стены символом наружу; электрика рисуется своими компонентами
 * (ElectricalItemShape выбирает розетку, выключатель или коробку), точки водоснабжения —
 * лёгким WaterPointShape без подложки и укрупнения, остальная сантехника — общим PlumbingItemShape. Вынесен из слоя, потому что тем же компонентом рисуется
 * призрак прибора под курсором при установке.
 */
import type { PointerEvent } from "react";
import { fixturePlanPoint, wallAngle, type Fixture, type WallGraph } from "../../plan";
import { getCatalogItem, isWaterPoint } from "@/shared/catalog";
import {
	ElectricalItemShape,
	PlumbingItemShape,
	WaterPointShape,
	symbolRenderSize,
} from "@/widgets/catalog";
import { CatalogSectionKind, itemDimensions } from "@/shared/plan";

const DEG = 180 / Math.PI;

/** На плане розетка мельче стены — символ приходится укрупнять, иначе не разглядеть. */
const FIXTURE_SCALE = 1.8;
const FIXTURE_MIN_PX = 16;
/** Точки водоснабжения мельче и стоят кучно — им хватает меньшей нижней границы, иначе наезжают друг на друга. */
const WATER_MIN_PX = 8;
/**
 * Объекты шире этого (умывальник, полотенцесушитель) на плане рисуются в натуральную величину
 * и без нижней границы в пикселях: иначе при отдалении узкая глубина упирается в минимум и тянет
 * за собой всю ширину — полотенцесушитель разрастается на полсанузла.
 */
const FIXTURE_TRUE_SIZE_FROM = 300;
const isTrueSize = (width: number) => width >= FIXTURE_TRUE_SIZE_FROM;
const fixtureScale = (width: number) => (isTrueSize(width) ? 1 : FIXTURE_SCALE);
const fixtureMinPx = (width: number) => (isTrueSize(width) ? 0 : FIXTURE_MIN_PX);

interface Props {
	graph: WallGraph;
	fixture: Fixture;
	mmPerPx: number;
	selected: boolean;
	/** Электрика без кабеля или точка воды без трубы, пока включён соответствующий слой */
	unconnected?: boolean;
	interactive?: boolean;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const WallItemShape2D = ({
	graph,
	fixture,
	mmPerPx,
	selected,
	unconnected = false,
	interactive = false,
	onPointerDown,
}: Props) => {
	const wall = graph.walls[fixture.wallId];
	const point = fixturePlanPoint(graph, fixture);
	if (!wall || !point) return null;
	const info = getCatalogItem(fixture.ref);
	const dimensions = info ? itemDimensions(fixture, info) : null;
	const deg = wallAngle(graph, wall) * DEG;
	// Символ рисуется «полукругом вверх», поэтому для дальней грани
	// его зеркалим — знак всегда смотрит из стены в комнату.
	const flip = fixture.side === 1 ? -1 : 1;
	const width = dimensions?.width ?? 80;
	const electrical =
		fixture.ref.catalog === CatalogSectionKind.Electrical ||
		fixture.ref.catalog === CatalogSectionKind.Lighting;
	const waterPoint = !electrical && isWaterPoint(info);
	// У изменяемой сантехники габариты на плане должны быть натуральными: экранный
	// минимум допустим только для условных значков электрики и водоточек.
	const exactSize = !electrical && !waterPoint;
	const zoom = exactSize ? 1 : fixtureScale(width);
	const minPx = exactSize || electrical ? 0 : fixtureMinPx(width);
	// Электрика на плане — условный значок «ширина × высота»; сантехника видна сверху
	// и занимает от стены свою глубину, иначе полотенцесушитель накроет унитаз
	const planDepth = electrical ? (info?.height ?? 80) : (dimensions?.depth ?? 80);
	const size = waterPoint
		? symbolRenderSize(width, planDepth, mmPerPx, 1, WATER_MIN_PX).height
		: symbolRenderSize(width, planDepth, mmPerPx, zoom, minPx).height;

	return (
		<g
			transform={`translate(${point.x} ${point.y}) rotate(${deg}) scale(1 ${flip}) translate(0 ${-size / 2})`}
		>
			{waterPoint ? (
				<WaterPointShape
					fixture={fixture}
					item={info}
					selected={selected}
					unconnected={unconnected}
					mmPerPx={mmPerPx}
					interactive={interactive}
					minPx={WATER_MIN_PX}
					onPointerDown={onPointerDown}
				/>
			) : electrical ? (
				<ElectricalItemShape
					fixture={fixture}
					item={info}
					selected={selected}
					unconnected={unconnected}
					mmPerPx={mmPerPx}
					interactive={interactive}
					scale={zoom}
					minPx={minPx}
					onPointerDown={onPointerDown}
				/>
			) : (
				<PlumbingItemShape
					fixture={fixture}
					item={info}
					selected={selected}
					unconnected={unconnected}
					mmPerPx={mmPerPx}
					interactive={interactive}
					scale={zoom}
					minPx={minPx}
					onPointerDown={onPointerDown}
				/>
			)}
		</g>
	);
};
